import {
  ALL_SINS,
  type CarnivalAction,
  type SinChoice,
} from "@cloudnest/carnival-of-sins-engine";

/**
 * Typed replay-event adapter for Carnival of Sins (죄악의 카니발).
 *
 * Almost no hidden information: dice, revealed sins, hand counts, and score
 * history are all public. The replay fixture's `init` carries the full
 * canonical state; `SelectSin`/`RevealSin` events replay through the engine
 * reducer exactly as they were produced. The replay screen still renders the
 * masked viewer projection by default (see ReplayScreen) so a not-yet-revealed
 * pending selection never reaches the DOM outside the recap toggle.
 */
export type CarnivalOfSinsReplayEventType = "init" | "SelectSin" | "RevealSin";

export const CARNIVAL_OF_SINS_REPLAY_EVENT_TYPES = [
  "init",
  "SelectSin",
  "RevealSin",
] as const satisfies readonly CarnivalOfSinsReplayEventType[];

export function normalizeCarnivalOfSinsReplayEventType(
  eventType: string,
): CarnivalOfSinsReplayEventType {
  if (
    (CARNIVAL_OF_SINS_REPLAY_EVENT_TYPES as readonly string[]).includes(
      eventType,
    )
  ) {
    return eventType as CarnivalOfSinsReplayEventType;
  }
  throw new Error(`Unsupported carnival-of-sins event type: ${eventType}`);
}

function requirePlayerId(payload: Record<string, unknown>): string {
  if (typeof payload.playerId !== "string" || payload.playerId.length === 0) {
    throw new Error(
      `Replay event payload is missing a valid playerId: ${JSON.stringify(payload)}`,
    );
  }
  return payload.playerId;
}

function requireChoice(payload: Record<string, unknown>): SinChoice {
  const choice = payload.choice;
  if (
    typeof choice !== "object" ||
    choice === null ||
    !("sin" in choice) ||
    !(ALL_SINS as readonly string[]).includes(
      (choice as { sin: unknown }).sin as string,
    )
  ) {
    throw new Error(
      `Replay event payload has an invalid RevealSin choice: ${JSON.stringify(payload)}`,
    );
  }
  return choice as SinChoice;
}

export function carnivalOfSinsEventToAction(
  eventType: string,
  payload: Record<string, unknown>,
): CarnivalAction {
  const type = normalizeCarnivalOfSinsReplayEventType(eventType);
  if (type === "init") {
    throw new Error(
      "carnivalOfSinsEventToAction cannot convert the init event",
    );
  }
  const playerId = requirePlayerId(payload);
  if (type === "SelectSin") {
    if (!(ALL_SINS as readonly string[]).includes(payload.sin as string)) {
      throw new Error(
        `Replay event payload has an invalid sin: ${JSON.stringify(payload)}`,
      );
    }
    return {
      type: "SelectSin",
      playerId,
      sin: payload.sin as (typeof ALL_SINS)[number],
    };
  }
  return { type: "RevealSin", playerId, choice: requireChoice(payload) };
}
